import { IBaseModel } from "../BaseModel";

/** Mirrors Rubble.Core.Entities.CoreDeploymentRunStatus; serialised by name. */
export type CoreDeploymentRunStatus =
  | "Scheduled"
  | "WaitingForStaging"
  | "PreparingPullRequests"
  | "Merging"
  | "Deploying"
  | "Succeeded"
  | "Failed"
  | "Cancelled";

/** Mirrors Rubble.Core.Entities.CoreDeploymentTargetStatus. */
export type CoreDeploymentTargetStatus =
  | "Pending"
  | "NothingToDeploy"
  | "Merged"
  | "MergeFailed"
  | "DeploySucceeded"
  | "DeployFailed"
  | "Skipped";

export type CoreRepo = "Api" | "Dashboard";

export type CoreTargetBranch = "core-deployment" | "tme-deployment";

export interface ICoreDeploymentTarget extends IBaseModel {
  repo: CoreRepo;
  targetBranch: CoreTargetBranch;
  status: CoreDeploymentTargetStatus;
  pullRequestNumber?: number | null;
  pullRequestUrl?: string | null;
  mergeSha?: string | null;
  mergedAt?: string | null;
  message?: string | null;
  /** JSON string of ICoreWorkflowSummaryItem[] written by the worker. */
  workflowSummary?: string | null;
}

export interface ICoreDeploymentRun extends IBaseModel {
  scheduledFor: string;
  deployCore: boolean;
  deployTme: boolean;
  notes?: string | null;
  status: CoreDeploymentRunStatus;
  requestedByEmail: string;
  requestedByName?: string | null;
  cancelledByEmail?: string | null;
  cancelledAt?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  stagingApiSha?: string | null;
  stagingDashboardSha?: string | null;
  failureReason?: string | null;
  targets: ICoreDeploymentTarget[];
}

export interface ICoreWorkflowSummaryItem {
  id: number;
  name: string;
  path: string;
  status: string;
  conclusion: string | null;
  html_url: string;
}

export interface ScheduleCoreDeploymentRequest {
  /** ISO 8601 UTC. */
  scheduledFor: string;
  deployCore: boolean;
  deployTme: boolean;
  notes?: string;
}

/** Mirrors Rubble.Core.Entities.CoreDeploymentOverviewStatus. */
export type CoreDeploymentOverviewStatus = "Pending" | "Generated" | "NoChanges" | "Failed";

/** The AI write-up of what a run deployed. status is null until one has been written. */
export interface ICoreDeploymentOverview {
  runId: string;
  status: CoreDeploymentOverviewStatus | null;
  internalText?: string | null;
  customerText?: string | null;
  /** Staff's edit of the customer text; kept across regenerations. */
  customerTextEdited?: string | null;
  hasCustomerFacingChanges: boolean;
  editedByEmail?: string | null;
  editedAt?: string | null;
  model?: string | null;
  generatedAt?: string | null;
  /** null when the worker wrote it after the run completed. */
  generatedByEmail?: string | null;
  commitCount: number;
  commitsTruncated: boolean;
  error?: string | null;
  isAiConfigured: boolean;
  isGitHubConfigured: boolean;
}

export const terminalRunStatuses: CoreDeploymentRunStatus[] = ["Succeeded", "Failed", "Cancelled"];

export function isRunTerminal(status: CoreDeploymentRunStatus): boolean {
  return terminalRunStatuses.includes(status);
}

/** A run can be cancelled until the worker starts opening pull requests. */
export function canCancelRun(status: CoreDeploymentRunStatus): boolean {
  return status === "Scheduled" || status === "WaitingForStaging";
}

export function parseWorkflowSummary(summary?: string | null): ICoreWorkflowSummaryItem[] {
  if (!summary) return [];
  try {
    const parsed = JSON.parse(summary);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}
